/**
 * LiveConsultation — Consulta guiada por IA en vivo.
 *
 * Flujo:
 *   1. Al montar solicita permiso de micrófono.
 *   2. Web Speech API transcribe en vivo (español, continuo, interim results).
 *   3. Cada ~8s de nueva transcripción se envía al backend /ai-consultation/analyze
 *      que devuelve síntomas, vitales, diagnóstico, notas y recetas.
 *   4. El formulario se auto-rellena mientras el doctor habla.
 *   5. Dos botones: MANUAL (cae al editor clásico) y FINALIZAR CONSULTA
 *      (guarda diagnosis + navega al preview de receta).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import DiagnosisInput from "../../components/DiagnosisInput";
import { SymptomSearch } from "../../components/SymptomSearch";
import {
  ArrowLeft,
  Sparkles,
  Mic,
  MicOff,
  Pause,
  Play,
  Loader2,
  ShieldAlert,
  Cake,
  Phone,
  Mail,
  Droplet,
  Ear,
  Headphones,
  User,
} from "@/components/icons";

// ── Helpers ─────────────────────────────────────────────────────────────
function calcAge(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  } catch {
    return null;
  }
}

const SYMPTOM_LIB = [
  "Fiebre",
  "Dolor de cabeza",
  "Tos",
  "Dolor de garganta",
  "Congestión nasal",
  "Dificultad para respirar",
  "Dolor en el pecho",
  "Náuseas",
  "Vómitos",
  "Diarrea",
  "Dolor abdominal",
  "Dolor muscular",
  "Fatiga",
  "Mareo",
  "Pérdida de apetito",
  "Insomnio",
  "Ansiedad",
  "Dolor articular",
];

// ── Detección local instantánea (sin llamar al servidor) ────────────────
// Palabras clave por síntoma canónico. Sensible al español dominicano.
const SYMPTOM_KEYWORDS = {
  "Fiebre": /\b(fiebre|calentura|febril|febr[íi]cula)\b/i,
  "Tos": /\btos\b/i,
  "Dolor de cabeza": /\b(dolor de cabeza|cefalea|jaqueca|migra[nñ]a)\b/i,
  "Dolor de garganta": /\b(dolor de garganta|garganta irritada|odinofagia)\b/i,
  "Congestión nasal": /\b(congesti[óo]n nasal|nariz tapada|congestionad[oa])\b/i,
  "Dificultad para respirar": /\b(dificultad (para )?respirar|falta de aire|disnea|ahogo)\b/i,
  "Dolor en el pecho": /\b(dolor (en el|de) pecho|dolor tor[áa]cico|opresi[óo]n en el pecho)\b/i,
  "Náuseas": /\b(n[áa]useas?|ganas de vomitar|asco)\b/i,
  "Vómitos": /\b(v[óo]mit[oa]s?|vomit[óoa]|arcadas?)\b/i,
  "Diarrea": /\bdiarrea\b/i,
  "Dolor abdominal": /\b(dolor abdominal|dolor de (barriga|est[óo]mago|vientre|panza)|c[óo]lico)\b/i,
  "Dolor muscular": /\b(dolor muscular|mialgia|dolor en los m[úu]sculos)\b/i,
  "Fatiga": /\b(fatiga|cansancio|agotamiento|debilidad general)\b/i,
  "Mareo": /\b(mareo|v[ée]rtigo|sensaci[óo]n de desmayo)\b/i,
  "Pérdida de apetito": /\b(p[ée]rdida de apetito|inapetencia|no tiene hambre|sin apetito)\b/i,
  "Insomnio": /\b(insomnio|no puede dormir|dificultad para dormir)\b/i,
  "Ansiedad": /\bansiedad\b/i,
  "Dolor articular": /\b(dolor articular|dolor en (las )?articulaciones|artralgia)\b/i,
};

// Extractor local ligero: encuentra síntomas confirmados/negados + vitales.
// Reglas:
//   1. Si hay negación ("no", "sin", "niega"...) dentro de los 25 chars previos
//      al síntoma → DENIED (se remueve si estaba).
//   2. Si es una pregunta ("has tenido", "tuv[oa]", "siente"...) sin respuesta
//      afirmativa → PENDING (se guarda hasta escuchar "sí/no" en la próxima
//      oración).
//   3. En otro caso → CONFIRMED.
const NEG_RX = /(?:^|[^a-záéíóúñ])(no|sin|niega|niego|ningun[oa]?|jam[áa]s|nunca)(?:[^a-záéíóúñ]|$)/i;
const AFF_RX = /(?:^|[^a-záéíóúñ])(s[íi]|claro|correcto|efectivamente|afirmativo|as[íi] es|por supuesto|exacto|positivo)(?:[^a-záéíóúñ]|$)/i;
const QUESTION_OPENER_RX = /(?:^|[^a-záéíóúñ])(ha tenido|has tenido|tuv[oa]|tuviste|tuvieron|siente|sientes|tiene alguna|ha presentado|acaso|hubo|habr[áa])(?:[^a-záéíóúñ]|$)/i;

function localExtract(text) {
  if (!text || text.length < 3) return { confirmed: [], denied: [], vitals: {} };
  const norm = text.toLowerCase();

  // Vitales (mismo comportamiento: sí/no no aplica a números).
  const vitals = {};
  let m = norm.match(/(\d{2,3})\s*(?:\/|sobre)\s*(\d{2,3})/);
  if (m) vitals.blood_pressure = `${m[1]}/${m[2]}`;
  m = norm.match(/(\d{2}(?:[.,]\d)?)\s*(?:°\s*c\b|grados?)/);
  if (m) vitals.temperature = m[1].replace(",", ".");
  m =
    norm.match(/(\d{2,3})\s*(?:bpm|latidos|pulsaciones|por minuto de pulso)/) ||
    norm.match(/(?:pulso|frecuencia cardiaca|latidos)\s*(?:de\s*)?(\d{2,3})/);
  if (m) vitals.heart_rate = m[1];
  m =
    norm.match(/(\d{2,3})\s*(?:%|por ciento)\s*(?:de\s*)?(?:ox[íi]geno|saturaci[óo]n|o2)/) ||
    norm.match(/(?:saturaci[óo]n|ox[íi]geno|o2)\s*(?:de\s*)?(\d{2,3})\s*(?:%|por ciento)?/);
  if (m) vitals.oxygen_saturation = m[1];
  m = norm.match(/(\d{1,2})\s*(?:respiraciones|rpm|por minuto de respiraci[óo]n)/);
  if (m) vitals.respiration_rate = m[1];
  m = norm.match(/(\d{2,3}(?:[.,]\d)?)\s*(?:kilos?|kg|kilogramos?)/);
  if (m) vitals.weight = m[1].replace(",", ".");

  // Síntomas — partimos por oración para inspeccionar contexto.
  const sentences = norm.split(/[.!?¿¡;\n]+/);
  const confirmed = new Set();
  const denied = new Set();
  let pending = null; // { symptom, age }

  for (const raw of sentences) {
    const s = raw.trim();
    if (s.length < 2) continue;

    let hadSymptomHere = false;
    for (const [name, rx] of Object.entries(SYMPTOM_KEYWORDS)) {
      const match = rx.exec(s);
      if (!match) continue;
      hadSymptomHere = true;
      const before = s.slice(Math.max(0, match.index - 30), match.index);
      const negated = NEG_RX.test(before);
      const isQuestion = QUESTION_OPENER_RX.test(before);
      if (negated) {
        denied.add(name);
        confirmed.delete(name);
        pending = null;
      } else if (isQuestion) {
        // esperamos la respuesta en las próximas oraciones
        pending = { symptom: name, age: 0 };
      } else {
        // afirmación directa: "tengo fiebre", "presenta fiebre", etc.
        if (!denied.has(name)) confirmed.add(name);
      }
    }

    // Si esta oración no menciona síntoma pero hay uno pendiente,
    // interpretamos la respuesta corta ("sí"/"no").
    if (!hadSymptomHere && pending) {
      pending.age += 1;
      const isAff = AFF_RX.test(s) && !NEG_RX.test(s);
      const isNeg = NEG_RX.test(s) && !AFF_RX.test(s);
      if (isAff) {
        confirmed.add(pending.symptom);
        denied.delete(pending.symptom);
        pending = null;
      } else if (isNeg) {
        denied.add(pending.symptom);
        confirmed.delete(pending.symptom);
        pending = null;
      } else if (pending.age >= 2) {
        pending = null; // se pierde el hilo después de 2 oraciones
      }
    }
  }

  return { confirmed: [...confirmed], denied: [...denied], vitals };
}

// Devuelve el objeto sin llaves con string vacío (para merges).
function pruneEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== "" && v != null) out[k] = v;
  }
  return out;
}

// ── Speech recognition wrapper ──────────────────────────────────────────
function getRecognitionCtor() {
  return (
    (typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
    null
  );
}

// ── Component ───────────────────────────────────────────────────────────
export default function LiveConsultation() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [appointment, setAppointment] = useState(null);
  const [patient, setPatient] = useState(null);
  const [permission, setPermission] = useState("idle"); // idle|granted|denied|unsupported
  const [phase, setPhase] = useState("idle"); // idle|listening|paused|analyzing|saving
  const [finalTx, setFinalTx] = useState("");
  const [interim, setInterim] = useState("");
  const lastSentCharsRef = useRef(0);

  // Formulario auto-generado
  const [symptoms, setSymptoms] = useState(new Set());
  const [customSymptoms, setCustomSymptoms] = useState([]);
  useEffect(() => {
    api.get("/doctor/symptoms").then(({ data }) => setCustomSymptoms(data || [])).catch(() => {});
  }, []);
  const [vitals, setVitals] = useState({});
  const [diagnosis, setDiagnosis] = useState("");
  const [treatment, setTreatment] = useState("");
  const [notes, setNotes] = useState("");
  const [medications, setMedications] = useState([]);
  const [studies, setStudies] = useState([]);
  const [followUp, setFollowUp] = useState("");
  const [confidence, setConfidence] = useState(0);

  const [saving, setSaving] = useState(false);
  // Ema sugiere diagnóstico desde los síntomas registrados
  const [suggestingDx, setSuggestingDx] = useState(false);
  const [dxDifferentials, setDxDifferentials] = useState([]);
  const suggestDiagnosisFromSymptoms = async () => {
    const list = Array.from(symptoms || []).filter(Boolean);
    if (list.length === 0) {
      toast.error("Registra al menos un síntoma para que Ema sugiera un diagnóstico.");
      return;
    }
    setSuggestingDx(true);
    try {
      const { data } = await api.post("/ai-consultation/diagnosis-suggest", {
        symptoms: list,
        patient_id: appointment?.patient_id || undefined,
      });
      if (data?.diagnosis) {
        markEdited("diagnosis");
        setDiagnosis(data.diagnosis);
      }
      setDxDifferentials(Array.isArray(data?.differentials) ? data.differentials : []);
      toast.success("Ema sugirió un diagnóstico — revísalo antes de guardar.");
    } catch (err) {
      toast.error(
        formatErr(err?.response?.data?.detail) || "No se pudo sugerir el diagnóstico.",
      );
    } finally {
      setSuggestingDx(false);
    }
  };
  const recognitionRef = useRef(null);
  const analyzeTimer = useRef(null);
  const pauseTimerRef = useRef(null);
  const analyzingRef = useRef(false);
  const runAnalyzeRef = useRef(null);
  const finalTxRef = useRef("");
  // Síntomas rechazados por el paciente (o quitados manualmente) — no re-agregar.
  const deniedSymptomsRef = useRef(new Set());
  // Campos que el doctor editó manualmente — la IA NO los sobreescribe.
  const userEditedRef = useRef({});
  const markEdited = (field) => {
    userEditedRef.current[field] = true;
  };
  // Guardamos el último interim+final agregado para pasar al análisis on-pause.
  const latestTextRef = useRef("");

  // ── Cargar cita + paciente ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data: list } = await api.get("/appointments");
        const appt = (list || []).find((a) => a.id === appointmentId);
        if (!appt) {
          toast.error("Cita no encontrada");
          return;
        }
        setAppointment(appt);
        if (appt.patient_id) {
          const { data: pat } = await api.get(`/patients/${appt.patient_id}`);
          setPatient(pat);
        }
      } catch {
        toast.error("No pude cargar la consulta");
      }
    })();
  }, [appointmentId]);

  // ── Verificar soporte (sin auto-iniciar). El doctor pulsa "Escuchar
  // conversación" para solicitar permiso + iniciar reconocimiento.
  useEffect(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setPermission("unsupported");
      return;
    }
    setPermission("idle");
    return () => {
      stopRecognition();
      if (analyzeTimer.current) clearInterval(analyzeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler explícito del botón "Escuchar conversación"
  const requestMicAndStart = async () => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setPermission("unsupported");
      return;
    }
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermission("granted");
      startRecognition();
    } catch {
      setPermission("denied");
    }
  };

  const startRecognition = () => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "es-DO";
    rec.onresult = (event) => {
      let interimTx = "";
      let finalAdd = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const res = event.results[i];
        if (res.isFinal) {
          finalAdd += res[0].transcript + " ";
        } else {
          interimTx += res[0].transcript;
        }
      }
      let mergedFinal = null;
      if (finalAdd) {
        setFinalTx((prev) => {
          mergedFinal = (prev + finalAdd).replace(/\s+/g, " ").trim() + " ";
          finalTxRef.current = mergedFinal;
          return mergedFinal;
        });
      }
      setInterim(interimTx);

      // (1) Detección local instantánea sobre final+interim actualizados.
      const combined = (finalTxRef.current + " " + interimTx).trim();
      // El ref siempre guarda final + interim más reciente para el pause-analyzer.
      latestTextRef.current = combined;
      const found = localExtract(combined);
      // Aplicar CONFIRMADOS (respetando lo negado) y DENEGADOS (borra + bloquea).
      if (found.confirmed.length || found.denied.length) {
        setSymptoms((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const s of found.denied) {
            deniedSymptomsRef.current.add(s);
            if (next.delete(s)) changed = true;
          }
          for (const s of found.confirmed) {
            if (deniedSymptomsRef.current.has(s)) continue;
            if (!next.has(s)) {
              next.add(s);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
      if (Object.keys(found.vitals).length) {
        setVitals((prev) => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(found.vitals)) {
            // No sobrescribir si el doctor ya lo editó o si tiene valor manual.
            if (!userEditedRef.current[`vitals.${k}`] && !prev[k]) {
              merged[k] = v;
            }
          }
          return merged;
        });
      }

      // (3) Detector de pausa: si hay 1.2s sin nueva voz, dispara analyze ya.
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = setTimeout(() => {
        runAnalyzeRef.current?.({ minDelta: 30 });
      }, 1200);
    };
    rec.onend = () => {
      // Auto-restart if we're still supposed to be listening (Chrome timeout)
      if (recognitionRef.current === rec && phase !== "paused") {
        try {
          rec.start();
        } catch {}
      }
    };
    rec.onerror = () => {};
    try {
      rec.start();
    } catch {}
    recognitionRef.current = rec;
    setPhase("listening");
  };

  const stopRecognition = () => {
    const rec = recognitionRef.current;
    if (rec) {
      try {
        rec.onend = null;
        rec.stop();
      } catch {}
      recognitionRef.current = null;
    }
  };

  const togglePause = () => {
    if (phase === "paused") {
      startRecognition();
    } else {
      stopRecognition();
      setPhase("paused");
    }
  };

  // ── Análisis IA reactivo (3s tick + on-pause) ────────────────────────
  useEffect(() => {
    if (permission !== "granted") return;

    // Definimos runAnalyze accesible por ref → interval + pause timer.
    runAnalyzeRef.current = async ({ minDelta = 40 } = {}) => {
      if (analyzingRef.current) return;
      const currentTx = latestTextRef.current || finalTx;
      const delta = currentTx.length - lastSentCharsRef.current;
      if (delta < minDelta) return;
      analyzingRef.current = true;
      lastSentCharsRef.current = currentTx.length;
      setPhase((p) => (p === "listening" ? "analyzing" : p));
      try {
        const { data } = await api.post(
          "/ai-consultation/analyze",
          { transcript: currentTx, patient_id: appointment?.patient_id },
          { timeout: 45000 },
        );
        applyExtracted(data?.extracted || {});
      } catch {
        /* silencioso — próximo tick reintenta */
      } finally {
        analyzingRef.current = false;
        setPhase((p) => (p === "analyzing" ? "listening" : p));
      }
    };

    analyzeTimer.current = setInterval(() => {
      runAnalyzeRef.current?.({ minDelta: 40 });
    }, 3000);

    return () => {
      if (analyzeTimer.current) clearInterval(analyzeTimer.current);
      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, appointment?.patient_id]);

  const applyExtracted = (ex) => {
    if (!ex) return;
    const edited = userEditedRef.current;

    // Síntomas: unión, respetando los que el paciente negó / doctor quitó.
    if (Array.isArray(ex.symptoms)) {
      setSymptoms((prev) => {
        const s = new Set(prev);
        let changed = false;
        ex.symptoms.forEach((sym) => {
          const norm = typeof sym === "string" ? sym : sym?.name || sym?.text;
          if (!norm) return;
          const key = String(norm);
          if (deniedSymptomsRef.current.has(key)) return;
          if (!s.has(key)) {
            s.add(key);
            changed = true;
          }
        });
        return changed ? s : prev;
      });
    }
    // Vitales: solo llenar los que estén vacíos y no editados manualmente.
    if (ex.vitals && typeof ex.vitals === "object") {
      setVitals((prev) => {
        const merged = { ...prev };
        for (const [k, v] of Object.entries(ex.vitals)) {
          if (v == null || v === "") continue;
          if (edited[`vitals.${k}`]) continue;
          if (!prev[k]) merged[k] = String(v);
        }
        return merged;
      });
    }
    // Texto libre: no pisa lo que el doctor haya tocado.
    if (ex.diagnosis && !edited.diagnosis) setDiagnosis(String(ex.diagnosis));
    if (ex.treatment && !edited.treatment) setTreatment(String(ex.treatment));
    const noteVal = ex.clinical_notes || ex.notes;
    if (noteVal && !edited.notes) setNotes(String(noteVal));
    if (ex.follow_up && !edited.followUp) setFollowUp(String(ex.follow_up));
    if (typeof ex.confidence === "number") setConfidence(ex.confidence);

    // Recetas: merge por nombre del medicamento (case-insensitive).
    const rawRx = ex.prescriptions || ex.prescription_items || ex.medications;
    if (Array.isArray(rawRx)) {
      const incoming = rawRx
        .map((m) => ({
          medication: m.drug || m.medication || m.name || "",
          dosage: m.dose || m.dosage || "",
          frequency: m.frequency || "",
          duration: m.duration || "",
          instructions: m.instructions || m.notes || "",
        }))
        .filter((m) => m.medication);
      if (incoming.length) {
        setMedications((prev) => {
          const byName = new Map(
            prev.map((m) => [m.medication.trim().toLowerCase(), m]),
          );
          for (const m of incoming) {
            const key = m.medication.trim().toLowerCase();
            const existing = byName.get(key);
            byName.set(key, existing ? { ...existing, ...pruneEmpty(m) } : m);
          }
          return Array.from(byName.values());
        });
      }
    }
    // Estudios: unión sin duplicados.
    if (Array.isArray(ex.recommended_studies)) {
      setStudies((prev) => {
        const seen = new Set(prev.map((s) => s.toLowerCase().trim()));
        const merged = [...prev];
        for (const st of ex.recommended_studies) {
          if (!st) continue;
          const k = String(st).toLowerCase().trim();
          if (!seen.has(k)) {
            seen.add(k);
            merged.push(String(st));
          }
        }
        return merged;
      });
    }
  };

  // ── Guardar y navegar a receta ───────────────────────────────────────
  const finalize = async () => {
    if (saving) return;
    if (!diagnosis.trim()) {
      toast.error("Aún no hay diagnóstico. Habla un poco más o edita manual.");
      return;
    }
    setSaving(true);
    stopRecognition();
    setPhase("saving");
    try {
      const chosenSymptoms = Array.from(symptoms);
      const symptomsText = chosenSymptoms.join(", ");
      await api.put(`/appointments/${appointmentId}`, {
        patient_id: appointment.patient_id,
        office_id: appointment.office_id,
        date: appointment.date,
        duration_min: appointment.duration_min,
        reason: appointment.reason,
        status: "completed",
      });
      await api.post("/diagnoses", {
        patient_id: appointment.patient_id,
        diagnosis: diagnosis.trim(),
        symptoms: symptomsText || undefined,
        treatment: treatment.trim() || undefined,
        notes: notes.trim() || undefined,
        vitals: Object.keys(vitals).length ? vitals : undefined,
        specialty: user?.specialty || undefined,
      });
      navigate(`/doctor/consulta/${appointmentId}/receta`, {
        state: {
          aiRxItems: medications,
          aiFollowUp: followUp,
          patientId: appointment.patient_id,
          patientName: patient?.full_name || "",
          diagnosis: diagnosis.trim(),
        },
      });
    } catch (err) {
      toast.error(
        "No pude guardar la consulta: " + formatErr(err.response?.data?.detail),
      );
      setSaving(false);
      setPhase("listening");
    }
  };

  const goManual = () => {
    stopRecognition();
    // Pasar el estado actual como query para que Consultation.jsx (clásico)
    // reciba los datos ya recopilados y el doctor sólo edite.
    navigate(`/doctor/consulta/${appointmentId}/manual`, {
      state: {
        prefilled: {
          symptoms: Array.from(symptoms),
          vitals,
          diagnosis,
          treatment,
          notes,
          medications,
          studies,
          followUp,
          transcript: finalTx,
        },
      },
    });
  };

  const addSymptom = (name) => {
    deniedSymptomsRef.current.delete(name);
    setSymptoms((prev) => new Set(prev).add(name));
  };

  const toggleSymptom = (name) => {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
        // Bloquear: la IA no lo re-agregará aunque siga oyéndolo.
        deniedSymptomsRef.current.add(name);
      } else {
        next.add(name);
        deniedSymptomsRef.current.delete(name);
      }
      return next;
    });
  };

  const orbState = useMemo(() => {
    if (permission === "denied") return "denied";
    if (permission === "unsupported") return "unsupported";
    if (permission === "idle") return "idle";
    if (phase === "paused") return "paused";
    if (phase === "analyzing") return "analyzing";
    if (phase === "saving") return "saving";
    return "listening";
  }, [permission, phase]);

  if (!appointment) {
    return (
      <div className="flex-1 flex items-center justify-center text-[13px] text-black/50">
        Cargando…
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4 pb-16" data-testid="live-consultation">
      <div className="space-y-3 lg:sticky lg:top-4 self-start">
        <PatientSidePanel patient={patient} appointment={appointment} />

        {/* ── Ema AI Panel (debajo del perfil) ──────────────────── */}
        <EmaAssistantCard
          orbState={orbState}
          onStart={requestMicAndStart}
          onTogglePause={togglePause}
          mode="live"
        />
      </div>

      <div className="min-w-0 space-y-3">
        <div className="flex items-center justify-between">
          <Link
            to="/doctor/agenda"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-black/60 hover:text-black transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            Volver a la agenda
          </Link>
          <span className="text-[10px] uppercase tracking-widest font-semibold text-black/40">
            Confianza IA: {Math.round((confidence || 0) * 100)}%
          </span>
        </div>

        {/* ── Transcripción (solo cuando está activa) ───────────────── */}
        {(orbState === "listening" || orbState === "paused" || orbState === "analyzing" || finalTx || interim) && (
          <div className="bg-[#F5F5F7] rounded-[24px] p-5" data-testid="live-ai-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Ear className="w-4 h-4 text-black/60" strokeWidth={1.75} weight="regular" />
                <div className="text-[13px] font-semibold text-black">Transcripción en vivo</div>
              </div>
              {(orbState === "listening" || orbState === "analyzing" || orbState === "paused") && (
                <button
                  onClick={togglePause}
                  className="w-8 h-8 rounded-full bg-white hover:bg-black/[0.06] text-black/70 flex items-center justify-center transition"
                  title={orbState === "paused" ? "Reanudar" : "Pausar"}
                  data-testid="live-toggle-pause"
                >
                  {orbState === "paused" ? (
                    <Play className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                  ) : (
                    <Pause className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                  )}
                </button>
              )}
            </div>
            <div
              className="bg-white rounded-xl p-4 max-h-40 overflow-y-auto no-scrollbar text-[13px] text-black/70 leading-relaxed"
              data-testid="live-transcript"
            >
              {finalTx || interim ? (
                <>
                  <span>{finalTx}</span>
                  <span className="text-black/40 italic">{interim}</span>
                </>
              ) : (
                <span className="text-black/40">
                  Aquí verás la transcripción en vivo mientras la consulta avanza…
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Auto-generated form ──────────────────────────────────── */}
        <div className="grid md:grid-cols-2 gap-3">
          {/* Síntomas */}
          <div className="bg-[#F5F5F7] rounded-[24px] p-5">
            <div className="text-[13px] font-semibold text-black mb-3">Síntomas</div>
            <SymptomSearch
              library={SYMPTOM_LIB}
              custom={customSymptoms}
              selected={symptoms}
              onPick={addSymptom}
              onCreated={(doc) =>
                setCustomSymptoms((prev) =>
                  prev.some((c) => c.id === doc.id) ? prev : [...prev, doc]
                )
              }
              onDeleted={(doc) => setCustomSymptoms((prev) => prev.filter((c) => c.id !== doc.id))}
            />
            <div className="flex flex-wrap gap-1.5" data-testid="symptom-pills">
              {[...SYMPTOM_LIB, ...customSymptoms.map((c) => c.name).filter((n) => !SYMPTOM_LIB.includes(n))].map((s) => {
                const active = symptoms.has(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSymptom(s)}
                    data-testid={`symptom-pill-${s}`}
                    className={`text-[12px] font-semibold px-3 py-1.5 rounded-full transition ${
                      active
                        ? "bg-[#0071E3] text-white"
                        : "bg-white text-black/60 hover:bg-black/[0.03]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
              {/* Adicionales detectados por IA que no estaban en la librería */}
              {Array.from(symptoms)
                .filter((s) => !SYMPTOM_LIB.includes(s) && !customSymptoms.some((c) => c.name === s))
                .map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSymptom(s)}
                    className="text-[12px] font-semibold px-3 py-1.5 rounded-full bg-[#0071E3] text-white transition"
                  >
                    {s} ×
                  </button>
                ))}
            </div>
          </div>

          {/* Signos vitales */}
          <div className="bg-[#F5F5F7] rounded-[24px] p-5">
            <div className="text-[13px] font-semibold text-black mb-3">Signos vitales</div>
            <div className="grid grid-cols-2 gap-2">
              <VitalInput
                label="Presión"
                unit="mmHg"
                value={vitals.blood_pressure}
                onChange={(v) => {
                  markEdited("vitals.blood_pressure");
                  setVitals({ ...vitals, blood_pressure: v });
                }}
              />
              <VitalInput
                label="Pulso"
                unit="bpm"
                value={vitals.heart_rate}
                onChange={(v) => {
                  markEdited("vitals.heart_rate");
                  setVitals({ ...vitals, heart_rate: v });
                }}
              />
              <VitalInput
                label="Temp."
                unit="°C"
                value={vitals.temperature}
                onChange={(v) => {
                  markEdited("vitals.temperature");
                  setVitals({ ...vitals, temperature: v });
                }}
              />
              <VitalInput
                label="Sat. O₂"
                unit="%"
                value={vitals.oxygen_saturation}
                onChange={(v) => {
                  markEdited("vitals.oxygen_saturation");
                  setVitals({ ...vitals, oxygen_saturation: v });
                }}
              />
              <VitalInput
                label="Respiración"
                unit="rpm"
                value={vitals.respiration_rate}
                onChange={(v) => {
                  markEdited("vitals.respiration_rate");
                  setVitals({ ...vitals, respiration_rate: v });
                }}
              />
              <VitalInput
                label="Peso"
                unit="kg"
                value={vitals.weight}
                onChange={(v) => {
                  markEdited("vitals.weight");
                  setVitals({ ...vitals, weight: v });
                }}
              />
            </div>
          </div>
        </div>

        {/* Diagnóstico + tratamiento + notas */}
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-[#F5F5F7] rounded-[24px] p-5">
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block">
                Diagnóstico
              </label>
              <button
                type="button"
                onClick={suggestDiagnosisFromSymptoms}
                disabled={suggestingDx || (symptoms?.size ?? 0) === 0}
                data-testid="live-dx-suggest"
                title={
                  (symptoms?.size ?? 0) === 0
                    ? "Registra al menos un síntoma primero"
                    : "Ema sugerirá un diagnóstico probable desde los síntomas"
                }
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0071E3] hover:text-[#0062c4] disabled:text-black/30 disabled:cursor-not-allowed transition"
              >
                <Sparkles
                  className={`w-3.5 h-3.5 ${suggestingDx ? "animate-pulse" : ""}`}
                  strokeWidth={1.75}
                  weight="regular"
                />
                {suggestingDx ? "Ema está pensando…" : "Sugerir con Ema"}
              </button>
            </div>
            <DiagnosisInput
              value={diagnosis}
              onChange={(v) => {
                markEdited("diagnosis");
                setDiagnosis(v);
              }}
              placeholder="Escribe o selecciona un diagnóstico…"
              testid="live-diagnosis"
            />
            {dxDifferentials.length > 0 && (
              <div
                className="mt-2 flex flex-wrap items-center gap-1.5"
                data-testid="live-dx-differentials"
              >
                <span className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                  Diferenciales
                </span>
                {dxDifferentials.map((d, i) => (
                  <button
                    key={`${d}-${i}`}
                    type="button"
                    onClick={() => {
                      markEdited("diagnosis");
                      setDiagnosis(d);
                    }}
                    className="inline-flex items-center text-[11px] font-medium bg-white hover:bg-[#0071E3]/10 hover:text-[#0071E3] text-black/75 rounded-full px-2.5 py-1 border border-black/[0.06] transition"
                    data-testid={`live-dx-diff-${i}`}
                    title="Usar como diagnóstico"
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
            <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5 mt-4">
              Tratamiento
            </label>
            <textarea
              value={treatment}
              onChange={(e) => {
                markEdited("treatment");
                setTreatment(e.target.value);
              }}
              rows={3}
              placeholder="Plan terapéutico sugerido por Ema…"
              className="w-full rounded-[14px] border border-black/[0.06] bg-white px-3 py-2 text-[13px] text-black placeholder:text-black/35 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition resize-none"
              data-testid="live-treatment"
            />
          </div>
          <div className="bg-[#F5F5F7] rounded-[24px] p-5">
            <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1.5">
              Notas clínicas
            </label>
            <textarea
              value={notes}
              onChange={(e) => {
                markEdited("notes");
                setNotes(e.target.value);
              }}
              rows={5}
              placeholder="Notas SOAP o de seguimiento…"
              className="w-full rounded-[14px] border border-black/[0.06] bg-white px-3 py-2 text-[13px] text-black placeholder:text-black/35 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition resize-none"
              data-testid="live-notes"
            />
            {followUp && (
              <div className="mt-2 text-[11px] text-black/60">
                <span className="font-semibold text-black/70">Seguimiento:</span> {followUp}
              </div>
            )}
          </div>
        </div>

        {/* Recetas + estudios */}
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-[#F5F5F7] rounded-[24px] p-5" data-testid="live-medications">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-[13px] font-semibold text-black">Recetas sugeridas</div>
              <div className="text-[11px] font-semibold text-black/40">({medications.length})</div>
            </div>
            {medications.length === 0 ? (
              <div className="text-[12px] text-black/45">
                Aún no hay medicamentos. Ema los agrega si mencionas alguno.
              </div>
            ) : (
              <div className="space-y-2">
                {medications.map((m, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-white px-3 py-2 text-[13px]"
                  >
                    <div className="font-semibold text-black">{m.medication}</div>
                    <div className="text-black/60 text-[12px]">
                      {[m.dosage, m.frequency, m.duration].filter(Boolean).join(" · ") || "Pendiente de confirmar"}
                    </div>
                    {m.instructions && (
                      <div className="text-[11px] text-black/50 mt-0.5">
                        {m.instructions}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#F5F5F7] rounded-[24px] p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="text-[13px] font-semibold text-black">Estudios sugeridos</div>
              <div className="text-[11px] font-semibold text-black/40">({studies.length})</div>
            </div>
            {studies.length === 0 ? (
              <div className="text-[12px] text-black/45">
                Ema propondrá análisis o estudios cuando haya síntomas suficientes.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {studies.map((st, i) => (
                  <li key={i} className="text-[13px] text-black/70">
                    <span className="text-black font-semibold">·</span> {st}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* ── Bottom actions (sticky) ─────────────────────────────── */}
        <div className="sticky bottom-0 bg-[#F5F5F7] rounded-[24px] px-5 py-3 flex items-center justify-end gap-2 mt-2">
          <button
            onClick={finalize}
            disabled={saving}
            data-testid="live-finalize-btn"
            className="h-10 px-5 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[13px] font-semibold transition disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} weight="regular" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            )}
            Finalizar consulta
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ema AI Panel (compact, estilo sidebar) ─────────────────────────────
function EmaAssistantCard({ orbState, onStart, onTogglePause, mode = "live" }) {
  const isManual = mode === "manual";
  const titleMap = {
    idle: "Listo para escuchar",
    asking: "Permitiendo micrófono…",
    denied: "Permiso denegado",
    unsupported: "Navegador no compatible",
    paused: "Grabación en pausa",
    listening: "Escuchando la consulta…",
    analyzing: "Analizando con IA…",
    saving: "Guardando consulta…",
  };
  const descMap = {
    idle: isManual
      ? "Puedes activar a Ema en cualquier momento para que escuche y rellene los campos."
      : "Pulsa el botón para que Ema empiece a transcribir y rellenar la consulta.",
    denied: "Habilita el micrófono en tu navegador o completa los campos manualmente.",
    unsupported: "Tu navegador no soporta reconocimiento de voz.",
    listening: "Habla con tu paciente. Todo se transcribe en tiempo real.",
    analyzing: "Extrayendo síntomas, vitales, diagnóstico y receta…",
    paused: "Reanuda cuando estés listo.",
  };

  return (
    <div className="bg-[#F5F5F7] rounded-[24px] p-5" data-testid="ema-assistant-card">
      <div className="flex flex-col items-center text-center">
        <AiOrb state={orbState} />
        <div className="mt-3 text-[10px] uppercase tracking-widest font-semibold text-black/45">
          Ema · Asistente IA
        </div>
        <div className="text-[14px] font-semibold text-black leading-tight mt-1">
          {titleMap[orbState] || "Ema"}
        </div>
        {descMap[orbState] && (
          <div className="text-[12px] text-black/55 mt-1.5 leading-snug">
            {descMap[orbState]}
          </div>
        )}
      </div>

      <div className="mt-4">
        {(orbState === "idle" || orbState === "denied") && (
          <button
            onClick={onStart}
            data-testid="live-start-listening"
            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white text-[12.5px] font-semibold transition"
          >
            <Ear className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            {orbState === "denied" ? "Reintentar" : "Escuchar conversación"}
          </button>
        )}
        {(orbState === "listening" || orbState === "analyzing" || orbState === "paused") && (
          <button
            onClick={onTogglePause}
            data-testid="live-toggle-pause"
            className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-white hover:bg-black/[0.03] text-[12.5px] font-semibold text-black transition"
          >
            {orbState === "paused" ? (
              <>
                <Play className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                Reanudar
              </>
            ) : (
              <>
                <Pause className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                Pausar
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ── AI Orb ──────────────────────────────────────────────────────────────
function AiOrb({ state }) {
  const isActive = state === "listening" || state === "analyzing";
  const isIdle = state === "idle";
  return (
    <div className="relative flex-shrink-0" data-testid={`ai-orb-${state}`}>
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center relative transition ${
          isIdle ? "bg-white" : "bg-black"
        } ${isActive ? "" : isIdle ? "" : "opacity-70"}`}
      >
        {state === "analyzing" || state === "saving" ? (
          <Loader2 className="w-6 h-6 text-white animate-spin" strokeWidth={1.75} weight="regular" />
        ) : state === "denied" || state === "unsupported" ? (
          <MicOff className="w-6 h-6 text-white" strokeWidth={1.75} weight="regular" />
        ) : isIdle ? (
          <Ear className="w-6 h-6 text-[#0071E3]" strokeWidth={1.75} weight="regular" />
        ) : (
          <Sparkles className="w-6 h-6 text-white" strokeWidth={1.75} weight="regular" />
        )}
        {state === "listening" && (
          <>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="absolute inset-0 rounded-2xl ring-2 ring-black/10 animate-ping" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Vital input ─────────────────────────────────────────────────────────
function VitalInput({ label, unit, value, onChange }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
        {label}
      </div>
      <div className="relative">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 rounded-[14px] border border-black/[0.06] bg-white pl-3 pr-12 text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 transition"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-black/40 uppercase tracking-widest font-semibold">
          {unit}
        </span>
      </div>
    </label>
  );
}

// ── Patient side panel ──────────────────────────────────────────────────
function PatientSidePanel({ patient, appointment }) {
  const age = calcAge(patient?.birth_date);
  const initials = (patient?.full_name || "P")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <aside
      className="rounded-[24px] p-5 bg-[#F5F5F7]"
      data-testid="patient-side-panel"
    >
      <div className="flex flex-col items-center text-center">
        {patient?.photo_url ? (
          <img
            src={patient.photo_url}
            alt={patient.full_name}
            className="w-20 h-20 rounded-full object-cover"
          />
        ) : (
          <div className="w-20 h-20 rounded-full flex items-center justify-center text-black text-[20px] font-semibold bg-white">
            {initials}
          </div>
        )}
        <p className="mt-3 text-[10px] uppercase tracking-widest font-semibold text-black/45">
          Paciente
        </p>
        <h3 className="text-[16px] font-semibold text-black leading-tight mt-0.5">
          {patient?.full_name || "Paciente"}
        </h3>
        {age != null && (
          <p className="text-[12px] text-black/55 mt-0.5">
            {age} años{patient?.gender ? ` · ${patient.gender}` : ""}
          </p>
        )}
      </div>

      <div className="mt-4 space-y-2.5 text-[13px]">
        {patient?.document_id && <Field icon={User} label="Documento" value={patient.document_id} />}
        {patient?.birth_date && <Field icon={Cake} label="Nacimiento" value={patient.birth_date} />}
        {patient?.phone && <Field icon={Phone} label="Teléfono" value={patient.phone} />}
        {patient?.email && <Field icon={Mail} label="Email" value={patient.email} />}
        {patient?.blood_type && <Field icon={Droplet} label="Sangre" value={patient.blood_type} />}
      </div>

      {patient?.allergies && (
        <div className="mt-4 rounded-xl bg-[#FEF3F2] border border-[#FEE4E2] p-2.5 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-[#B42318] flex-shrink-0 mt-0.5" strokeWidth={1.75} weight="regular" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold text-[#B42318]">Alergias</p>
            <p className="text-[12px] text-[#B42318] leading-snug">{patient.allergies}</p>
          </div>
        </div>
      )}

      {appointment && (
        <div className="mt-4 rounded-xl bg-white p-2.5">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-black/45">Motivo</p>
          <p className="text-[13px] text-black font-semibold mt-0.5 truncate">
            {appointment.reason || "Consulta"}
          </p>
          <p className="text-[11px] text-black/50 mt-0.5">
            {new Date(appointment.date).toLocaleString("es-ES")}
          </p>
        </div>
      )}
    </aside>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-7 h-7 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-black/60" strokeWidth={1.75} weight="regular" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-black/40">{label}</div>
        <div className="text-[13px] text-black truncate">{value}</div>
      </div>
    </div>
  );
}
